import React, {Fragment} from 'react';
import {Map} from 'immutable';
import {flatten} from 'lodash';

import {emptyArray} from '@packages/utilities/fp/utils';
import {
  Environment,
  WebflowEnvironments,
} from '@packages/systems/core-constants';
import {withSystem, Plugins, Atom} from '@packages/systems/wfdl';
import {excludeSearchByDefaultType} from '@packages/systems/search';
import {
  iconName,
  qualify,
  pluginName,
  typeName,
} from '@packages/systems/wfdl-Qualified';
import withAttribute from './withAttribute';
import {
  DATA_ATTR_NODE_TYPE,
  NODE_TYPE_COMMERCE_CHECKOUT_SHIPPING_METHODS_LIST,
  SHIPPING_METHODS_STATES,
  WF_COLLECTION_DATA_KEY,
  WF_TEMPLATE_ID_DATA_KEY,
  WF_TEMPLATE_TYPE,
} from '@packages/systems/commerce/constants';
import {px} from '@packages/systems/core/utils/WebflowCssSubset';
import {createRgbaColor} from '@packages/systems/core/utils/ColorUtils';
import {getCommerceDataProperty} from './ContextUtils';

import type {
  AtomRenderProps,
  ChildRender,
  ContextType,
} from '@packages/systems/wfdl';

import {
  TListOfElement,
  TTypeConstructor,
  TRecord,
} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {useRuntimeEnvironment, useTemplate} from '@packages/systems/render';
import {
  getChildren,
  getInPojo,
  getTagOrElse,
} from '@packages/utilities/data-utils';

const {SSR, EXPORT} = WebflowEnvironments;

const styleDisplayNone = Map({style: Map({display: 'none'})});

const sampleShippingMethods = [
  {
    id: null,
    name: '',
    description: '',
    mode: '',
    price: {value: 0, unit: 'USD', string: '$0.00'},
  },
];

const getShippingMethods = getCommerceDataProperty(
  'availableShippingMethods',
  []
);

const shouldThisComponentBeShown = ({
  runtimeEnvironment,
  shippingMethodState,
}: {
  runtimeEnvironment: Environment;
  shippingMethodState: unknown;
}) => {
  const isDesigner = runtimeEnvironment === WebflowEnvironments.DESIGNER;

  if (isDesigner) {
    return shippingMethodState === SHIPPING_METHODS_STATES.DEFAULT;
  }
  return false;
};

const renderShippingMethod =
  ({
    context,
    renderChild,
    children,
  }: {
    context: ContextType;
    renderChild: ChildRender;
    children: unknown[];
  }) =>
  (item: unknown, itemIndex: number) =>
    children.map((child) =>
      renderChild(
        child,
        // TODO (ecommerce): Memoize commerce context mutations
        context.set('data', item),
        // hack; when using mockOrder, we don't get ids
        `shippingMethod-${itemIndex}`,
        itemIndex
      )
    );

const createTemplateId = (id: string) => `wf-template-${id}`;

const renderShippingMethods = (
  {renderChild, context, data}: AtomRenderProps,
  availableShippingMethods: Array<unknown>
) => {
  const renderShippingMethodWithContext = renderShippingMethod({
    context,
    renderChild,
    children: getChildren(data),
  });

  return availableShippingMethods.length > 0
    ? flatten(availableShippingMethods.map(renderShippingMethodWithContext))
    : null;
};

const CommerceCheckoutShippingMethodsList = Atom.createRender((prevProps) => {
  const {
    attributes,
    classes,
    data,
    context: prevContext,
    generateAttributes,
    elementId,
  } = prevProps;
  const availableShippingMethods = getShippingMethods(prevContext);
  // TODO (ecommerce): Memoize commerce context mutations
  const context = prevContext
    .update('bindingPathPrefix', emptyArray, (bindingPathPrefix) =>
      bindingPathPrefix.concat('availableShippingMethods[]')
    )
    //.set('insideCommerceShippingMethodsList', true);
    .set('insideCommerceOrderShippingMethodsList', true);

  const props = {...prevProps, context} as const;

  const runtimeEnvironment = useRuntimeEnvironment();
  const shippingMethodState: unknown = context.get('shippingMethodState');

  const styledAttributes = shouldThisComponentBeShown({
    runtimeEnvironment,
    shippingMethodState,
  })
    ? attributes
    : attributes.merge(styleDisplayNone);

  const template = useTemplate();

  const Tag = getTagOrElse(getInPojo(data, ['tag']), 'div');
  if (runtimeEnvironment === SSR || runtimeEnvironment === EXPORT) {
    const content = renderShippingMethods(props, sampleShippingMethods);
    const templateContent = template(content);
    const tagAttributes = {
      ...generateAttributes(attributes, classes),
      [WF_COLLECTION_DATA_KEY]:
        'database.commerceOrder.availableShippingMethods',
      [WF_TEMPLATE_ID_DATA_KEY]: createTemplateId(elementId),
    } as const;
    return (
      <Fragment>
        <script type={WF_TEMPLATE_TYPE} id={createTemplateId(elementId)}>
          {templateContent}
        </script>
        <Tag {...tagAttributes}>{content}</Tag>
      </Fragment>
    );
  } else {
    return (
      <Tag {...generateAttributes(styledAttributes, classes)}>
        {renderShippingMethods(props, availableShippingMethods)}
      </Tag>
    );
  }
});

const config = createIntegratedAtom({
  displayName: 'Shipping Method List',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Dynamo'), iconName('CollectionList')),
  },
  showEmpty: true,
  constraints: {
    ancestors: [
      {is: 'CommerceCheckoutShippingMethodsWrapper', rule: 'ExactlyOne'},
    ],
    wraps: ['CommerceCheckoutShippingMethodItem'],
  },
  style: {
    base: {
      main: {
        noPseudo: {
          borderLeftColor: createRgbaColor('e6e6e6'),
          borderLeftWidth: px(1),
          borderLeftStyle: 'solid',
          borderRightColor: createRgbaColor('e6e6e6'),
          borderRightWidth: px(1),
          borderRightStyle: 'solid',
        },
      },
    },
  },
  render: withAttribute(
    DATA_ATTR_NODE_TYPE,
    NODE_TYPE_COMMERCE_CHECKOUT_SHIPPING_METHODS_LIST
  )(CommerceCheckoutShippingMethodsList),
  fields: {
    search: withSystem(
      {private: true},
      TRecord({
        exclude: excludeSearchByDefaultType,
      })
    ),
    tag: withSystem(
      {
        label: 'Tag',
        private: true,
        defaultValue: 'div',
      },
      TTypeConstructor(qualify(pluginName('Basic'), typeName('TagsDropdown')))
    ),
    children: TListOfElement,
  },
});

export default config;
